import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import aviImage from '../../images/אבי לוי.png';
import styles from './Me.module.css';

gsap.registerPlugin(ScrollTrigger);

// CountUp Component
const CountUp = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let startTime;
          const startValue = 0;
          const endValue = parseInt(end);

          const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
            
            // Easing function - easeOutExpo
            const easeOutExpo = 1 - Math.pow(2, -10 * progress);
            const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutExpo);
            
            setCount(currentValue);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(endValue);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
};

const AboutAvi = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const contentRef = useRef(null);
  
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    city: '',
    experience: '',
    goal: '',
    timeCommitment: '',
    disciplineAware: '',
    openQuestion: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const serverUrl = "https://dynamic-server-dfc88e1f1c54.herokuapp.com/leads/newLead";
  const reciver = "riseupnon@gmail.com";

  const handleWhatsAppClick = () => {
    setShowContactForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'נא להזין שם מלא';
    }

    const phoneRegex = /^0(5\d|[23489])\d{7}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'נא להזין מספר טלפון';
    } else if (!phoneRegex.test(formData.phone.replace(/[-\s]/g, ''))) {
      newErrors.phone = 'נא להזין מספר טלפון תקין';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'נא להזין אימייל';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'נא להזין אימייל תקין';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'נא להזין עיר';
    }

    if (!formData.experience) {
      newErrors.experience = 'נא לבחור תשובה';
    }

    if (!formData.goal) {
      newErrors.goal = 'נא לבחור תשובה';
    }

    if (!formData.timeCommitment) {
      newErrors.timeCommitment = 'נא לבחור תשובה';
    }

    if (!formData.disciplineAware) {
      newErrors.disciplineAware = 'נא לבחור תשובה';
    }

    if (!agreed) {
      newErrors.agreed = 'יש לאשר את תנאי השימוש';
    }

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      setTimeout(() => {
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`) || 
                            document.querySelector(`.${styles.errorText}`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (errorElement.focus) {
            errorElement.focus();
          }
        }
      }, 100);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleContactSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    const detailedInfo = `
עיר: ${formData.city}
---
1. ניסיון במסחר: ${formData.experience}
2. מטרה עיקרית: ${formData.goal}
3. זמן שבועי ללמידה: ${formData.timeCommitment}
4. מודעות למשמעת נדרשת: ${formData.disciplineAware}
5. שאלה פתוחה: ${formData.openQuestion || 'לא צוין'}
    `.trim();

    const serverData = {
      name: formData.fullName,
      phone: formData.phone.replace(/[-\s]/g, ''),
      email: formData.email,
      reason: detailedInfo,
      reciver: reciver
    };

    try {
      const response = await fetch(serverUrl, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serverData)
      });

      if (response.ok) {
        setSubmitted(true);
        
        // מעבר לדף תודה אחרי 1.5 שניות
        setTimeout(() => {
          navigate('/תודה');
        }, 1500);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      alert("התרחשה שגיאה, אנא נסו שוב מאוחר יותר");
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Image entrance
      gsap.fromTo(
        imageRef.current,
        { opacity: 0, x: 60, scale: 0.9 },
        {
          opacity: 1,
          x: 0,
          scale: 1,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Content paragraphs stagger
      const paragraphs = contentRef.current?.querySelectorAll(`.${styles.storyParagraph}`);
      if (paragraphs) {
        gsap.fromTo(
          paragraphs,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            stagger: 0.15,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: contentRef.current,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }

      // Highlight box
      const highlightBox = contentRef.current?.querySelector(`.${styles.highlightBox}`);
      if (highlightBox) {
        gsap.fromTo(
          highlightBox,
          { opacity: 0, scale: 0.95 },
          {
            opacity: 1,
            scale: 1,
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: highlightBox,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }

      // Parallax on background
      gsap.to(`.${styles.bgGradient}`, {
        yPercent: 20,
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
        },
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className={styles.section} ref={sectionRef}>
      {/* Background Effects */}
      <div className={styles.bgGradient}></div>
      <div className={styles.gridOverlay}></div>
      
      <div className={styles.container}>
        {/* Section Header */}
        <div className={styles.sectionHeader}>
          <div className={styles.labelWrapper}>
            <span className={styles.labelLine}></span>
            <span className={styles.labelDot}></span>
            <span className={styles.labelText}>הסיפור שלי</span>
            <span className={styles.labelDot}></span>
            <span className={styles.labelLine}></span>
          </div>
          <h2 className={styles.sectionTitle}>
            נעים מאוד, <span className={styles.nameHighlight}>אבי לוי</span>
          </h2>
        </div>

        <div className={styles.mainContent}>
          {/* Image Side */}
          <div className={styles.imageSide} ref={imageRef}>
            <div className={styles.imageCard}>
              <div className={styles.imageWrapper}>
                <img src={aviImage} alt="אבי לוי" className={styles.image} />
              </div>
            </div>
            
            {/* Stats - Centered */}
            <div className={styles.statsContainer}>
              <div className={styles.statsRow}>
                <div className={styles.statItem}>
                  <span className={styles.statNumber}>
                    <CountUp end={20} duration={2} suffix="+" />
                  </span>
                  <span className={styles.statLabel}>שנות ניסיון</span>
                </div>
                <div className={styles.statDivider}></div>
                <div className={styles.statItem}>
                  <span className={styles.statNumber}>
                    <CountUp end={200} duration={2.5} suffix="+" />
                  </span>
                  <span className={styles.statLabel}>תלמידים</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div className={styles.contentSide} ref={contentRef}>
            <div className={styles.storyCard}>
              <p className={styles.storyParagraph}>
                הגעתי מעולם הרכב. עבדתי שנים כסוחר רכבים, סגרתי עסקאות גדולות ובניתי לעצמי שם. הייתי טוב במה שעשיתי, אבל תמיד ידעתי שאני רוצה יותר.
              </p>
              
              <p className={styles.storyParagraph}>
                יום אחד החלטתי לקחת את החוש העסקי שפיתחתי ולהיכנס לעולם חדש לגמרי - <strong>שוק ההון</strong>. התחלתי ללמוד, ניסיתי, טעיתי וחזרתי ללמוד. לאט לאט הבנתי איך הכסף הגדול באמת עובד ואיך לזהות הזדמנויות לפני כולם.
              </p>
              
              <p className={styles.storyParagraph}>
                היום אני סוחר בכסף אמיתי, כל יום מחדש. לא מדבר על תיאוריות - <strong>אני חי את זה</strong>. נכנס לשוק, מזהה הזדמנויות ופועל. את מה שלימדתי את עצמי אני רוצה להעביר גם לך.
              </p>
            </div>
            
            <div className={styles.highlightBox}>
              <div className={styles.highlightGlow}></div>
              <p className={styles.highlightText}>
                <strong>למה אני עושה את זה?</strong> כי אני יודע כמה קשה להתחיל לבד וכמה מידע מבלבל יש בחוץ. רציתי ליצור מקום שבו אנשים לומדים מניסיון אמיתי - לא מספרים ולא מיוטיוב, אלא <strong>מסוחר שעושה את זה כל יום.</strong>
              </p>
            </div>
            
            <div className={styles.storyCard}>
              <p className={styles.storyParagraph}>
                אני לא מבטיח התעשרות מהירה ולא עומד למכור לך חלומות ורודים. אני מציע לך ללמוד שיטה שעובדת, מאדם שמשתמש בה בעצמו. <strong>תראה בדיוק מה אני עושה, למה אני עושה את זה, ואיך גם אתה יכול לעשות את אותו הדבר.</strong>
              </p>
            </div>

            <div className={styles.ctaWrapper}>
              <button className={styles.ctaButton} onClick={handleWhatsAppClick}>
                <span className={styles.ctaButtonText}>אבי, אני רוצה להפוך לסוחר רווחי</span>
                <span className={styles.ctaButtonIcon}>
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Overlay */}
      <div className={`${styles.contactOverlay} ${showContactForm ? styles.active : ''}`}>
        <div className={styles.contactForm}>
          <button className={styles.closeButton} onClick={() => setShowContactForm(false)}>
            ×
          </button>

          <div className={styles.formHeader}>
            <h2 className={styles.formTitle}>מלא את הטופס וקבל התאמה אישית</h2>
            <p className={styles.formSubtitle}>למסלול מסחר מקצועי עם חשבון ממומן</p>
          </div>

          <div className={styles.formScrollContainer}>
            {/* Personal Details Section */}
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </span>
                פרטים אישיים
              </h3>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>שם מלא</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`${styles.formInput} ${errors.fullName ? styles.inputError : ''}`}
                    placeholder="השם המלא שלך"
                    disabled={isSubmitting || submitted}
                  />
                  {errors.fullName && <p className={styles.errorText}>{errors.fullName}</p>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>טלפון</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`${styles.formInput} ${errors.phone ? styles.inputError : ''}`}
                    placeholder="050-1234567"
                    disabled={isSubmitting || submitted}
                  />
                  {errors.phone && <p className={styles.errorText}>{errors.phone}</p>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>אימייל</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`${styles.formInput} ${errors.email ? styles.inputError : ''}`}
                    placeholder="your@email.com"
                    disabled={isSubmitting || submitted}
                  />
                  {errors.email && <p className={styles.errorText}>{errors.email}</p>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>עיר</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={`${styles.formInput} ${errors.city ? styles.inputError : ''}`}
                    placeholder="עיר מגורים"
                    disabled={isSubmitting || submitted}
                  />
                  {errors.city && <p className={styles.errorText}>{errors.city}</p>}
                </div>
              </div>
            </div>

            {/* Question 1 */}
            <div className={styles.formSection}>
              <h3 className={styles.questionTitle}>
                <span className={styles.questionNumber}>1</span>
                איזה ניסיון יש לך במסחר בשוק ההון?
              </h3>
              <div className={styles.radioGroup}>
                <label className={`${styles.radioOption} ${formData.experience === 'אין לי ניסיון בכלל' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="experience"
                    value="אין לי ניסיון בכלל"
                    checked={formData.experience === 'אין לי ניסיון בכלל'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>אין לי ניסיון בכלל</span>
                </label>
                <label className={`${styles.radioOption} ${formData.experience === 'סוחר מעט, אך עדיין לא עקבי' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="experience"
                    value="סוחר מעט, אך עדיין לא עקבי"
                    checked={formData.experience === 'סוחר מעט, אך עדיין לא עקבי'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>סוחר מעט, אך עדיין לא עקבי</span>
                </label>
                <label className={`${styles.radioOption} ${formData.experience === 'יש לי ניסיון ורוצה להתקדם למסחר מקצועי עם חשבונות ממומנים' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="experience"
                    value="יש לי ניסיון ורוצה להתקדם למסחר מקצועי עם חשבונות ממומנים"
                    checked={formData.experience === 'יש לי ניסיון ורוצה להתקדם למסחר מקצועי עם חשבונות ממומנים'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>יש לי ניסיון ורוצה להתקדם למסחר מקצועי עם חשבונות ממומנים</span>
                </label>
              </div>
              {errors.experience && <p className={styles.errorText}>{errors.experience}</p>}
            </div>

            {/* Question 2 */}
            <div className={styles.formSection}>
              <h3 className={styles.questionTitle}>
                <span className={styles.questionNumber}>2</span>
                מה המטרה העיקרית שלך בלימודי מסחר?
              </h3>
              <div className={styles.radioGroup}>
                <label className={`${styles.radioOption} ${formData.goal === 'יצירת הכנסה נוספת מהצד' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="goal"
                    value="יצירת הכנסה נוספת מהצד"
                    checked={formData.goal === 'יצירת הכנסה נוספת מהצד'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>יצירת הכנסה נוספת מהצד</span>
                </label>
                <label className={`${styles.radioOption} ${formData.goal === 'הפיכת המסחר לקריירה עיקרית' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="goal"
                    value="הפיכת המסחר לקריירה עיקרית"
                    checked={formData.goal === 'הפיכת המסחר לקריירה עיקרית'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>הפיכת המסחר לקריירה עיקרית</span>
                </label>
                <label className={`${styles.radioOption} ${formData.goal === 'עמידה בתנאי חשבון ממומן וקבלת הקצאת הון' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="goal"
                    value="עמידה בתנאי חשבון ממומן וקבלת הקצאת הון"
                    checked={formData.goal === 'עמידה בתנאי חשבון ממומן וקבלת הקצאת הון'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>עמידה בתנאי חשבון ממומן וקבלת הקצאת הון</span>
                </label>
              </div>
              {errors.goal && <p className={styles.errorText}>{errors.goal}</p>}
            </div>

            {/* Question 3 */}
            <div className={styles.formSection}>
              <h3 className={styles.questionTitle}>
                <span className={styles.questionNumber}>3</span>
                כמה זמן ריאלי אתה מוכן להקדיש ללמידה ולתרגול בשבוע?
              </h3>
              <div className={styles.radioGroup}>
                <label className={`${styles.radioOption} ${formData.timeCommitment === 'עד שעתיים' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="timeCommitment"
                    value="עד שעתיים"
                    checked={formData.timeCommitment === 'עד שעתיים'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>עד שעתיים</span>
                </label>
                <label className={`${styles.radioOption} ${formData.timeCommitment === '3-5 שעות' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="timeCommitment"
                    value="3-5 שעות"
                    checked={formData.timeCommitment === '3-5 שעות'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>3-5 שעות</span>
                </label>
                <label className={`${styles.radioOption} ${formData.timeCommitment === 'יותר מ-5 שעות' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="timeCommitment"
                    value="יותר מ-5 שעות"
                    checked={formData.timeCommitment === 'יותר מ-5 שעות'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>יותר מ-5 שעות</span>
                </label>
              </div>
              {errors.timeCommitment && <p className={styles.errorText}>{errors.timeCommitment}</p>}
            </div>

            {/* Question 4 */}
            <div className={styles.formSection}>
              <h3 className={styles.questionTitle}>
                <span className={styles.questionNumber}>4</span>
                האם ברור לך שמדובר בתהליך לימודי ומעשי הדורש משמעת, תרגול והתמדה לאורך זמן?
              </h3>
              <div className={styles.radioGroup}>
                <label className={`${styles.radioOption} ${formData.disciplineAware === 'כן' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="disciplineAware"
                    value="כן"
                    checked={formData.disciplineAware === 'כן'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>כן</span>
                </label>
                <label className={`${styles.radioOption} ${formData.disciplineAware === 'לא בטוח' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="disciplineAware"
                    value="לא בטוח"
                    checked={formData.disciplineAware === 'לא בטוח'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>לא בטוח</span>
                </label>
              </div>
              {errors.disciplineAware && <p className={styles.errorText}>{errors.disciplineAware}</p>}
            </div>

            {/* Question 5 - Open Question */}
            <div className={styles.formSection}>
              <h3 className={styles.questionTitle}>
                <span className={styles.questionNumber}>5</span>
                שאלה פתוחה
              </h3>
              <p className={styles.questionSubtext}>
                למה החלטת להתחיל ללמוד מסחר דווקא עכשיו, ומה היעד שלך בהתקדמות לחשבון ממומן?
              </p>
              <textarea
                name="openQuestion"
                value={formData.openQuestion}
                onChange={handleInputChange}
                className={styles.formTextarea}
                placeholder="ספר לנו..."
                rows="4"
                disabled={isSubmitting || submitted}
              />
            </div>

            {/* Privacy Checkbox */}
            <div className={styles.privacyCheckbox}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="agreed"
                  checked={agreed}
                  onChange={() => setAgreed(!agreed)}
                  disabled={isSubmitting || submitted}
                />
                <span className={styles.checkboxCustom}></span>
                <span>קראתי ואני מאשר/ת את תנאי השימוש ומדיניות הפרטיות</span>
              </label>
              {errors.agreed && <p className={styles.errorText}>{errors.agreed}</p>}
            </div>

            {/* Submit Button */}
            <div className={styles.formButtons}>
              <button
                className={`${styles.submitButton} ${isSubmitting ? styles.submitting : ''} ${submitted ? styles.submitted : ''}`}
                onClick={handleContactSubmit}
                disabled={isSubmitting || submitted}
              >
                {isSubmitting ? (
                  <>
                    <span className={styles.spinner}></span>
                    שולח...
                  </>
                ) : submitted ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="currentColor" className={styles.successIcon}>
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                    </svg>
                    נשלח בהצלחה!
                  </>
                ) : (
                  'שליחה וקבלת התאמה אישית'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutAvi;